import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllLicenses,
  searchLicenses,
  updateLicense,
  createNewLicense,
  getMigrationPayload,
  createV2License,
  updateV2License,
  getLicenseBaseUrl,
  getLicenseEnv,
  LicenseSearchField,
} from '../services/api';
import {
  LicenseDetails,
  LicenseUpdateRequest,
  NewLicenseRequest,
  NewV2LicenseRequest,
  V2LicenseUpdateRequest,
  LicenseBatch,
  AnyLicense,
  LicenseEnv,
  isV2License,
} from '../types';
import EnvBadge from './EnvBadge';
import RegionCombobox from './RegionCombobox';
import './LicenseScreen.css';

interface LicenseScreenProps {
  onBack: () => void;
}

// Human-readable messages for the migrate endpoint error codes.
const MIGRATE_ERRORS: Record<string, string> = {
  '120044': 'A v2 license already exists for this account. No migration is required.',
  '120045': 'No v1 license found for this account; nothing to migrate.',
  '120046': 'No deployment history found for this account; cannot determine deployment region.',
  '120047': "Customer's deployed RainMaker version does not support v2 licenses (needs 2.0.0+).",
  '120048': 'v1 license cannot be migrated (NO_LIMIT or invalid registered_nodes_limit).',
};

const LicenseScreen: React.FC<LicenseScreenProps> = ({ onBack }) => {
  const { isAuthenticated } = useAuth();
  const [licenses, setLicenses] = useState<AnyLicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [searchField, setSearchField] = useState<LicenseSearchField>('account_id');
  const [searchValue, setSearchValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  // The search that produced the current view, so we can refresh it after edits.
  const [currentSearch, setCurrentSearch] = useState<{ field: LicenseSearchField; value: string } | null>(null);
  const [licenseEnv, setLicenseEnvState] = useState<LicenseEnv>(getLicenseEnv());

  // Create flow
  const [showVersionChooser, setShowVersionChooser] = useState(false);
  const [createVersion, setCreateVersion] = useState<'v1' | 'v2' | null>(null);

  // Update flow
  const [editingV1, setEditingV1] = useState<LicenseDetails | null>(null);
  const [editingV2, setEditingV2] = useState<AnyLicense | null>(null);

  // Migration flow
  const [migration, setMigration] = useState<{ v1: AnyLicense; proposed: NewV2LicenseRequest } | null>(null);

  const clearMessages = () => {
    setError('');
    setInfo('');
  };

  const refreshCurrentView = async () => {
    if (currentSearch) {
      const response = await searchLicenses(currentSearch.field, currentSearch.value);
      setLicenses(response.data.licenses);
    } else {
      await loadAllLicenses();
    }
  };

  const loadAllLicenses = async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const response = await getAllLicenses();
      setLicenses(response.data.licenses);
      setCurrentSearch(null);
      clearMessages();
    } catch (err) {
      setError('Failed to load licenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const runSearch = async () => {
    const value = searchValue.trim();
    if (!isAuthenticated || !value) return;
    try {
      setLoading(true);
      const response = await searchLicenses(searchField, value);
      setLicenses(response.data.licenses);
      setCurrentSearch({ field: searchField, value });
      clearMessages();
    } catch (err) {
      setError('Failed to search licenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getLatestLicense = (): AnyLicense | undefined => {
    if (licenses.length === 0) return undefined;
    return licenses.reduce((latest, current) => {
      const latestTime = latest.creation_timestamp || 0;
      const currentTime = current.creation_timestamp || 0;
      return currentTime > latestTime ? current : latest;
    }, licenses[0]);
  };

  // Scope the view to a single account (used after create) and reflect it in the search box.
  const showLicensesForAccount = async (accountId?: string) => {
    const id = (accountId || '').trim();
    if (!id) {
      await loadAllLicenses();
      return;
    }
    try {
      setLoading(true);
      const response = await searchLicenses('account_id', id);
      setLicenses(response.data.licenses);
      setSearchField('account_id');
      setSearchValue(id);
      setCurrentSearch({ field: 'account_id', value: id });
      clearMessages();
    } catch (err) {
      setError('Failed to load the created license');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ---- Create ----
  const handleCreateClick = () => {
    clearMessages();
    setShowVersionChooser(true);
  };

  const handleCreateV1 = async (data: NewLicenseRequest) => {
    if (!isAuthenticated) return;
    try {
      await createNewLicense(data);
      setCreateVersion(null);
      await showLicensesForAccount(data.account_id);
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Failed to create v1 license');
    }
  };

  const handleCreateV2 = async (data: NewV2LicenseRequest) => {
    if (!isAuthenticated) return;
    try {
      await createV2License(data);
      setCreateVersion(null);
      await showLicensesForAccount(data.account_id);
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Failed to create v2 license');
    }
  };

  // ---- Update ----
  const handleUpdateClick = () => {
    clearMessages();
    const latest = getLatestLicense();
    if (!latest) return;
    if (isV2License(latest)) {
      setEditingV2(latest);
    } else {
      setEditingV1(latest as LicenseDetails);
    }
  };

  const handleUpdateV1 = async (data: LicenseUpdateRequest) => {
    if (!isAuthenticated) return;
    try {
      await updateLicense(data);
      setEditingV1(null);
      await refreshCurrentView();
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Failed to update v1 license');
    }
  };

  // Submit a v2 update (batches + fields). Secondary emails are managed separately via
  // immediate add/remove calls, so they are NOT part of this PUT.
  const handleUpdateV2 = async (accountId: string, base: V2LicenseUpdateRequest) => {
    if (!isAuthenticated) return;
    try {
      await updateV2License(accountId, base);
      setEditingV2(null);
      await refreshCurrentView();
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Failed to update v2 license');
    }
  };

  // Immediate secondary-email change on an existing v2 license (operation add/remove).
  // Returns true on success so the editor can update its displayed list.
  const commitSecondaryEmail = async (
    accountId: string,
    email: string,
    operation: 'add' | 'remove'
  ): Promise<boolean> => {
    if (!isAuthenticated) return false;
    try {
      clearMessages();
      await updateV2License(accountId, { secondary_emails: [email], operation });
      setInfo(`Secondary email ${operation === 'add' ? 'added' : 'removed'}: ${email}`);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.description || `Failed to ${operation} secondary email`);
      return false;
    }
  };

  // ---- Migration ----
  const startMigration = async (v1: AnyLicense) => {
    clearMessages();
    try {
      setLoading(true);
      const response = await getMigrationPayload(v1.account_id);
      setMigration({ v1, proposed: response.data });
    } catch (err: any) {
      const code = err?.response?.data?.error_code;
      setError(MIGRATE_ERRORS[code] || err?.response?.data?.description || 'Failed to build migration payload');
    } finally {
      setLoading(false);
    }
  };

  const submitMigration = async (data: NewV2LicenseRequest) => {
    try {
      await createV2License(data);
      setMigration(null);
      setInfo('Migration complete — v2 license created.');
      await showLicensesForAccount(data.account_id);
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Failed to create v2 license from migration');
    }
  };

  const latest = getLatestLicense();
  const latestIsV2 = latest ? isV2License(latest) : false;
  const visibleLicenses = showHistory ? licenses : latest ? [latest] : [];

  return (
    <div className="license-container">
      <header className="license-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>License Management</h1>
        <div className="header-buttons">
          <EnvBadge
            onEnvChange={(env) => {
              setLicenseEnvState(env);
              setLicenses([]);
              setCurrentSearch(null);
              clearMessages();
            }}
          />
          <button onClick={handleCreateClick} className="create-button">
            Create New License
          </button>
          {licenses.length > 0 && (
            <button onClick={handleUpdateClick} className="update-button">
              Update License
            </button>
          )}
        </div>
      </header>

      <div className="license-env-bar">
        License API ({licenseEnv}): <code>{getLicenseBaseUrl(licenseEnv)}</code>
      </div>

      <div className="license-controls">
        <div className="search-section">
          <select
            className="search-field"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as LicenseSearchField)}
          >
            <option value="account_id">Account ID</option>
            <option value="fwpn">FWPN</option>
            <option value="email">Email</option>
          </select>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder={
              searchField === 'fwpn' ? 'Search by FWPN' :
              searchField === 'email' ? 'Search by Email' : 'Search by Account ID'
            }
            className="search-input"
          />
          <button onClick={runSearch} className="search-button">Search</button>
          <button onClick={loadAllLicenses} className="refresh-button">Show All</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {info && <div className="info-message">{info}</div>}

      {loading ? (
        <div className="loading">Loading licenses...</div>
      ) : (
        <>
          {licenses.length > 0 && (
            <div className="view-controls">
              {licenses.length > 1 && (
                <button onClick={() => setShowHistory(!showHistory)} className="history-button">
                  {showHistory ? 'Show Latest Only' : `Show History (${licenses.length} total)`}
                </button>
              )}
              {currentSearch && !latestIsV2 && (
                <button onClick={() => startMigration(latest as AnyLicense)} className="migrate-button">
                  Migrate v1 → v2
                </button>
              )}
              <button onClick={() => setShowRaw(!showRaw)} className="raw-toggle">
                {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
              </button>
            </div>
          )}

          <div className="licenses-grid">
            {visibleLicenses.map((license) => {
              const isLatest = licenses.length > 1 && license === latest;
              const v2 = isV2License(license);
              return (
                <div
                  key={`${license.account_id}-${license.creation_timestamp}`}
                  className={`license-card ${isLatest ? 'license-card-latest' : ''}`}
                >
                  <div className="license-header-info">
                    <div className="license-title-container">
                      <h3>{license.account_id}</h3>
                      <span className={`version-badge version-badge-${v2 ? 'v2' : 'v1'}`}>
                        {v2 ? 'v2' : 'v1'}
                      </span>
                      {isLatest && <span className="latest-badge">Latest</span>}
                    </div>
                  </div>
                  {v2 ? (
                    <V2LicenseCard license={license} formatTimestamp={formatTimestamp} />
                  ) : (
                    <V1LicenseCard license={license} formatTimestamp={formatTimestamp} />
                  )}
                  {showRaw && <pre className="raw-json">{JSON.stringify(license, null, 2)}</pre>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Create: version chooser */}
      {showVersionChooser && (
        <VersionChooser
          onChoose={(v) => {
            setShowVersionChooser(false);
            setCreateVersion(v);
          }}
          onCancel={() => setShowVersionChooser(false)}
        />
      )}

      {/* Create v1 */}
      {createVersion === 'v1' && (
        <V1LicenseForm
          title="Create v1 License"
          onSubmit={handleCreateV1}
          onCancel={() => setCreateVersion(null)}
        />
      )}

      {/* Create v2 */}
      {createVersion === 'v2' && (
        <V2LicenseForm
          title="Create v2 License"
          mode="create"
          onSubmitCreate={handleCreateV2}
          onCancel={() => setCreateVersion(null)}
        />
      )}

      {/* Update v1 (with Migrate option) */}
      {editingV1 && (
        <V1LicenseForm
          title="Update v1 License"
          license={editingV1}
          onSubmit={handleUpdateV1}
          onCancel={() => setEditingV1(null)}
          onMigrate={() => {
            const lic = editingV1;
            setEditingV1(null);
            startMigration(lic as AnyLicense);
          }}
        />
      )}

      {/* Update v2 */}
      {editingV2 && (
        <V2LicenseForm
          title="Update v2 License"
          mode="update"
          license={editingV2}
          onSubmitUpdate={handleUpdateV2}
          onCommitEmail={commitSecondaryEmail}
          onCancel={() => setEditingV2(null)}
        />
      )}

      {/* Migration compare */}
      {migration && (
        <MigrationCompare
          v1={migration.v1}
          proposed={migration.proposed}
          formatTimestamp={formatTimestamp}
          onSubmit={submitMigration}
          onCancel={() => setMigration(null)}
        />
      )}
    </div>
  );
};

// ---------- Card renderers ----------

const V1LicenseCard: React.FC<{ license: AnyLicense; formatTimestamp: (t?: number) => string }> = ({
  license,
  formatTimestamp,
}) => (
  <>
    <p><strong>Email:</strong> {license.email}</p>
    <p><strong>Version:</strong> {license.version}</p>
    <p><strong>Registered Nodes Limit:</strong> {license.registered_nodes_limit}</p>
    <p><strong>Payment Mode:</strong> {license.payment_mode || 'N/A'}</p>
    <p><strong>Created:</strong> {formatTimestamp(license.creation_timestamp)}</p>
    <p><strong>Expires:</strong> {formatTimestamp(license.expiration_timestamp)}</p>
    {license.features && license.features.length > 0 && (
      <div className="features">
        <strong>Features:</strong>
        <ul>
          {license.features.map((feature, index) => (
            <li key={index}>
              {feature.name} -{feature.enabled ? ' Enabled' : ' Disabled'}
              {feature.quota ? ` (Quota: ${feature.quota})` : ''}
            </li>
          ))}
        </ul>
      </div>
    )}
  </>
);

const V2LicenseCard: React.FC<{ license: AnyLicense; formatTimestamp: (t?: number) => string }> = ({
  license,
  formatTimestamp,
}) => (
  <>
    {license.customer_name && <p><strong>Customer:</strong> {license.customer_name}</p>}
    <p><strong>Email:</strong> {license.email}</p>
    {license.secondary_emails && license.secondary_emails.length > 0 && (
      <p><strong>Cc:</strong> {license.secondary_emails.join(', ')}</p>
    )}
    <p><strong>Region:</strong> {license.region}</p>
    <p><strong>Payment Mode:</strong> {license.payment_mode || 'N/A'}</p>
    <p><strong>Total Nodes:</strong> {license.total_nodes_limit ?? license.registered_nodes_limit ?? 'N/A'}</p>
    {typeof license.total_matter_trial_quota === 'number' && (
      <p><strong>Matter Trial Quota:</strong> {license.total_matter_trial_quota}</p>
    )}
    {typeof license.total_camera_quota === 'number' && (
      <p><strong>Camera Quota:</strong> {license.total_camera_quota}</p>
    )}
    {license.matter_fabric_enabled && <p><strong>Matter Fabric:</strong> Enabled</p>}
    {license.fwpns && license.fwpns.length > 0 && (
      <p><strong>FWPNs:</strong> {license.fwpns.join(', ')}</p>
    )}
    <p><strong>Created:</strong> {formatTimestamp(license.creation_timestamp)}</p>
    <p><strong>Expires:</strong> {formatTimestamp(license.expiration_timestamp)}</p>
    {license.batches && license.batches.length > 0 && (
      <div className="features">
        <strong>Batches ({license.batches.length}):</strong>
        <table className="batch-table">
          <thead>
            <tr><th>#</th><th>Nodes</th><th>Exp(mo)</th><th>Status</th><th>Matter</th><th>Camera</th><th>FWPN</th></tr>
          </thead>
          <tbody>
            {license.batches.map((b, i) => (
              <tr key={b.batch_num ?? i}>
                <td>{b.batch_num ?? i + 1}</td>
                <td>{b.nodes_limit}</td>
                <td>{b.expiration_months ?? '-'}</td>
                <td>{b.status ?? 'active'}</td>
                <td>{b.matter_fabric_enabled ? '∞' : b.matter_trial_quota ?? '-'}</td>
                <td>{b.camera_quota ?? '-'}</td>
                <td>{b.fwpn ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>
);

// ---------- Version chooser ----------

const VersionChooser: React.FC<{
  onChoose: (v: 'v1' | 'v2') => void;
  onCancel: () => void;
}> = ({ onChoose, onCancel }) => (
  <div className="modal-overlay">
    <div className="modal-content version-chooser">
      <h2>Create New License</h2>
      <p>Choose the license version to create:</p>
      <div className="version-choice-grid">
        <button className="version-choice" onClick={() => onChoose('v1')}>
          <span className="version-badge version-badge-v1">v1</span>
          <span>Legacy license (features, no region). Created via POST.</span>
        </button>
        <button className="version-choice" onClick={() => onChoose('v2')}>
          <span className="version-badge version-badge-v2">v2</span>
          <span>Batch-based license (region required, multi-batch).</span>
        </button>
      </div>
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
      </div>
    </div>
  </div>
);

// ---------- v1 form ----------

interface V1FormProps {
  license?: LicenseDetails;
  title: string;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  onMigrate?: () => void;
}

const V1LicenseForm: React.FC<V1FormProps> = ({ license, title, onSubmit, onCancel, onMigrate }) => {
  const getFeature = (name: string) => license?.features?.find((f) => f.name === name) || null;
  const matterFeature = getFeature('matter');
  const matterTrialFeature = getFeature('matter_trial');
  const cameraFeature = getFeature('camera');

  const [formData, setFormData] = useState({
    account_id: license?.account_id || '',
    email: license?.email || '',
    registered_nodes_limit: license?.registered_nodes_limit || '',
    version: 'v1',
    expiration_months: license?.expiration_months?.toString() || '',
    payment_mode: license?.payment_mode || 'SUBSCRIPTION',
    matter: matterFeature?.enabled || false,
    matter_trial_enabled: matterTrialFeature?.enabled || false,
    matter_trial: matterTrialFeature?.quota?.toString() || '',
    camera_enabled: cameraFeature?.enabled || false,
    camera: cameraFeature?.quota?.toString() || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const features = [];
    if (formData.matter) features.push({ name: 'matter', enabled: true, quota: null });
    if (formData.matter_trial_enabled && parseInt(formData.matter_trial) > 0) {
      features.push({ name: 'matter_trial', enabled: true, quota: parseInt(formData.matter_trial) });
    }
    if (formData.camera_enabled && parseInt(formData.camera) > 0) {
      features.push({ name: 'camera', enabled: true, quota: parseInt(formData.camera) });
    }
    onSubmit({
      account_id: formData.account_id,
      email: formData.email,
      registered_nodes_limit: formData.registered_nodes_limit,
      activated_nodes_limit: formData.registered_nodes_limit,
      version: 'v1',
      expiration_months: parseInt(formData.expiration_months) || null,
      payment_mode: formData.payment_mode,
      features,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{title}</h2>
        <form onSubmit={handleSubmit} className="license-form">
          <input type="text" placeholder="Account ID" value={formData.account_id}
            onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
            required disabled={!!license} />
          <input type="email" placeholder="Email" value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value.replace(/\s/g, '') })} required />
          <input type="text" placeholder="Registered Nodes Limit" value={formData.registered_nodes_limit}
            onChange={(e) => setFormData({ ...formData, registered_nodes_limit: e.target.value })} required />
          <input type="number" placeholder="Expiration Months" value={formData.expiration_months}
            onChange={(e) => setFormData({ ...formData, expiration_months: e.target.value })} />
          <select value={formData.payment_mode}
            onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}>
            <option value="SUBSCRIPTION">SUBSCRIPTION</option>
            <option value="LICENSE">LICENSE</option>
          </select>

          <div className="features-section">
            <h4>Features</h4>
            <label className="feature-checkbox">
              <input type="checkbox" checked={formData.matter}
                onChange={(e) => setFormData({ ...formData, matter: e.target.checked })} />
              Matter Support (Unlimited)
            </label>
            <div className="feature-with-quota">
              <label className="feature-checkbox">
                <input type="checkbox" checked={formData.matter_trial_enabled}
                  onChange={(e) => setFormData({ ...formData, matter_trial_enabled: e.target.checked })} />
                Matter Trial
              </label>
              <input type="number" placeholder="Number of fabrics" value={formData.matter_trial}
                onChange={(e) => setFormData({ ...formData, matter_trial: e.target.value })}
                disabled={!formData.matter_trial_enabled} min="1" className="quota-input" />
            </div>
            <div className="feature-with-quota">
              <label className="feature-checkbox">
                <input type="checkbox" checked={formData.camera_enabled}
                  onChange={(e) => setFormData({ ...formData, camera_enabled: e.target.checked })} />
                Camera Support
              </label>
              <input type="number" placeholder="Number of cameras" value={formData.camera}
                onChange={(e) => setFormData({ ...formData, camera: e.target.value })}
                disabled={!formData.camera_enabled} min="1" className="quota-input" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
            {onMigrate && (
              <button type="button" onClick={onMigrate} className="migrate-button">Migrate to V2</button>
            )}
            <button type="submit" className="submit-button">{title}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------- Batch editor ----------

const emptyBatch = (): LicenseBatch => ({ nodes_limit: 0, status: 'active' });

// Shared editable fields for a single batch (used for new batches and opted-in existing edits).
const BatchFields: React.FC<{
  batch: LicenseBatch;
  onChange: (patch: Partial<LicenseBatch>) => void;
}> = ({ batch: b, onChange }) => (
  <div className="batch-fields">
    <label className="batch-checkbox">
      <input type="checkbox" checked={(b.status ?? 'active') === 'active'}
        onChange={(e) => onChange({ status: e.target.checked ? 'active' : 'inactive' })} />
      Active
    </label>
    <label>Nodes Limit
      <input type="number" value={b.nodes_limit}
        onChange={(e) => onChange({ nodes_limit: parseInt(e.target.value) || 0 })} min="0" required />
    </label>
    <label>Expiration (months)
      <input type="number" value={b.expiration_months ?? ''}
        onChange={(e) => onChange({ expiration_months: e.target.value ? parseInt(e.target.value) : undefined })} />
    </label>
    <label className="batch-checkbox">
      <input type="checkbox" checked={!!b.matter_fabric_enabled}
        onChange={(e) => onChange({ matter_fabric_enabled: e.target.checked })} />
      Matter Fabric (unlimited)
    </label>
    <label>Matter Trial Quota
      <input type="number" value={b.matter_trial_quota ?? ''}
        disabled={!!b.matter_fabric_enabled}
        onChange={(e) => onChange({ matter_trial_quota: e.target.value ? parseInt(e.target.value) : undefined })} />
    </label>
    <label>Camera Quota
      <input type="number" value={b.camera_quota ?? ''}
        onChange={(e) => onChange({ camera_quota: e.target.value ? parseInt(e.target.value) : undefined })} />
    </label>
    <label>Device Type
      <input type="text" value={b.device_type ?? ''}
        onChange={(e) => onChange({ device_type: e.target.value || undefined })} />
    </label>
    <label>Category
      <input type="number" value={b.category ?? ''}
        onChange={(e) => onChange({ category: e.target.value ? parseInt(e.target.value) : undefined })} />
    </label>
    <label>FWPN
      <input type="text" value={b.fwpn ?? ''}
        onChange={(e) => onChange({ fwpn: e.target.value || undefined })} />
    </label>
  </div>
);

const batchSummary = (b: LicenseBatch): string =>
  `Nodes: ${b.nodes_limit} · Exp: ${b.expiration_months ?? '-'}mo · ${b.status ?? 'active'}` +
  ` · Matter: ${b.matter_fabric_enabled ? '∞' : b.matter_trial_quota ?? '-'}` +
  ` · Camera: ${b.camera_quota ?? '-'} · FWPN: ${b.fwpn ?? '-'}`;

// Editor for NEW batches (no batch_num — the API creates them). Used in create + update.
const NewBatchEditor: React.FC<{
  batches: LicenseBatch[];
  onChange: (batches: LicenseBatch[]) => void;
  heading: string;
}> = ({ batches, onChange, heading }) => {
  const update = (i: number, patch: Partial<LicenseBatch>) =>
    onChange(batches.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => onChange(batches.filter((_, idx) => idx !== i));
  const add = () => onChange([...batches, emptyBatch()]);

  return (
    <div className="batch-editor">
      <div className="batch-editor-head">
        <h4>{heading}</h4>
        <button type="button" className="add-batch-button" onClick={add}>+ Add Batch</button>
      </div>
      {batches.length === 0 && <p className="batch-empty">No new batches added.</p>}
      {batches.map((b, i) => (
        <div key={i} className="batch-row">
          <div className="batch-row-head">
            <strong>New Batch {i + 1}</strong>
            <button type="button" className="remove-batch-button" onClick={() => remove(i)}>Remove</button>
          </div>
          <BatchFields batch={b} onChange={(patch) => update(i, patch)} />
        </div>
      ))}
    </div>
  );
};

type EditableBatch = LicenseBatch & { _modify?: boolean };

// Existing batches in update mode: read-only unless "Modify" is ticked (sent with batch_num).
const ExistingBatchEditor: React.FC<{
  batches: EditableBatch[];
  onChange: (batches: EditableBatch[]) => void;
}> = ({ batches, onChange }) => {
  const update = (i: number, patch: Partial<EditableBatch>) =>
    onChange(batches.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  if (batches.length === 0) return null;

  return (
    <div className="batch-editor">
      <h4>Existing Batches</h4>
      <p className="batch-hint">
        Read-only by default. Tick <strong>Modify</strong> to change a batch — only ticked batches
        are sent (by batch number); the rest are left untouched. Use “Add Batch” below to create new ones.
      </p>
      {batches.map((b, i) => (
        <div key={b.batch_num ?? i} className={`batch-row ${b._modify ? 'batch-row-editing' : ''}`}>
          <div className="batch-row-head">
            <strong>Batch #{b.batch_num}</strong>
            <label className="batch-status">
              <input type="checkbox" checked={!!b._modify}
                onChange={(e) => update(i, { _modify: e.target.checked })} />
              Modify
            </label>
          </div>
          {b._modify ? (
            <BatchFields batch={b} onChange={(patch) => update(i, patch)} />
          ) : (
            <div className="batch-readonly">{batchSummary(b)}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------- Secondary emails editor ----------

// Secondary emails editor.
// - Create mode (no onCommit): edits a local list, sent in the create POST.
// - Update mode (onCommit provided): each Add/× fires an immediate PUT with operation add/remove
//   (v2 update requires `operation`); the local list updates only on a successful call.
const SecondaryEmailsEditor: React.FC<{
  emails: string[];
  onChange: (emails: string[]) => void;
  onCommit?: (email: string, operation: 'add' | 'remove') => Promise<boolean>;
  // Reports the current (un-added) draft so the parent form can block submit on a forgotten "Add".
  onDraftChange?: (draft: string) => void;
}> = ({ emails, onChange, onCommit, onDraftChange }) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const updateDraft = (v: string) => { setDraft(v); onDraftChange?.(v); };

  const add = async () => {
    const v = draft.trim();
    if (!v || emails.includes(v)) { updateDraft(''); return; }
    if (onCommit) {
      setBusy(true);
      const ok = await onCommit(v, 'add');
      setBusy(false);
      if (!ok) return; // keep draft so the user can retry
      onChange([...emails, v]);
    } else {
      onChange([...emails, v]);
    }
    updateDraft('');
  };

  const remove = async (email: string) => {
    if (onCommit) {
      setBusy(true);
      const ok = await onCommit(email, 'remove');
      setBusy(false);
      if (!ok) return;
    }
    onChange(emails.filter((x) => x !== email));
  };

  return (
    <div className="secondary-emails">
      <h4>Secondary Emails (Cc)</h4>
      {onCommit && <p className="batch-hint">Changes are saved immediately.</p>}
      <ul className="email-list">
        {emails.map((e) => (
          <li key={e}>
            {e}
            <button type="button" className="remove-email" disabled={busy} onClick={() => remove(e)}>×</button>
          </li>
        ))}
      </ul>
      <div className="email-add">
        <input type="email" placeholder="add@company.com" value={draft} disabled={busy}
          onChange={(e) => updateDraft(e.target.value.replace(/\s/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} disabled={busy}>{busy ? '…' : 'Add'}</button>
      </div>
    </div>
  );
};

// ---------- v2 form (create + update) ----------

interface V2FormProps {
  title: string;
  mode: 'create' | 'update';
  license?: AnyLicense;
  prefill?: NewV2LicenseRequest;
  onCancel: () => void;
  onSubmitCreate?: (data: NewV2LicenseRequest) => void;
  onSubmitUpdate?: (accountId: string, base: V2LicenseUpdateRequest) => void;
  // Update mode only: immediately add/remove a secondary email (operation-based PUT).
  onCommitEmail?: (accountId: string, email: string, operation: 'add' | 'remove') => Promise<boolean>;
}

const V2LicenseForm: React.FC<V2FormProps> = ({
  title, mode, license, prefill, onCancel, onSubmitCreate, onSubmitUpdate, onCommitEmail,
}) => {
  const source = prefill || license;
  const [customerName, setCustomerName] = useState(source?.customer_name || '');
  const [accountId, setAccountId] = useState(source?.account_id || '');
  const [email, setEmail] = useState(source?.email || '');
  const [region, setRegion] = useState(source?.region || '');
  const [paymentMode, setPaymentMode] = useState(source?.payment_mode || 'LICENSE');
  const [registeredNodesLimit, setRegisteredNodesLimit] = useState(source?.registered_nodes_limit || '');
  const [secondaryEmails, setSecondaryEmails] = useState<string[]>(source?.secondary_emails || []);
  const isUpdate = mode === 'update';
  // Create (incl. migration prefill): all source batches are new. Update: start with none.
  const [newBatches, setNewBatches] = useState<LicenseBatch[]>(
    isUpdate ? [] : source?.batches ? [...source.batches] : []
  );
  // Update only: existing batches are read-only until "Modify" is ticked.
  const [existingBatches, setExistingBatches] = useState<EditableBatch[]>(
    isUpdate ? (source?.batches || []).map((b) => ({ ...b, _modify: false })) : []
  );
  const [sendEmail, setSendEmail] = useState(prefill?.send_email ?? true);
  // Un-added text in the secondary-email input; blocks submit so it isn't silently dropped.
  const [pendingEmail, setPendingEmail] = useState('');
  const [formError, setFormError] = useState('');

  const isLicenseMode = paymentMode === 'LICENSE';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingEmail.trim()) {
      setFormError('You typed a secondary email but didn\'t click "Add". Add it or clear the field before saving.');
      return;
    }
    setFormError('');
    if (mode === 'create' && onSubmitCreate) {
      const payload: NewV2LicenseRequest = {
        customer_name: customerName || undefined,
        account_id: accountId,
        email,
        region,
        version: 'v2',
        payment_mode: paymentMode,
        secondary_emails: secondaryEmails.length ? secondaryEmails : undefined,
        registered_nodes_limit: registeredNodesLimit || undefined,
        batches: isLicenseMode ? newBatches : undefined,
        send_email: sendEmail,
      };
      onSubmitCreate(payload);
    } else if (mode === 'update' && onSubmitUpdate) {
      // Send only the batches the admin touched: modified existing (with batch_num) + new (without).
      const editedExisting = existingBatches
        .filter((b) => b._modify)
        .map(({ _modify, ...b }) => b);
      const batchesToSend = [...editedExisting, ...newBatches];
      // Secondary emails are managed via immediate add/remove calls, not this PUT.
      const base: V2LicenseUpdateRequest = {
        customer_name: customerName || undefined,
        email: email || undefined,
        region: region || undefined,
        registered_nodes_limit: !isLicenseMode ? registeredNodesLimit || undefined : undefined,
        batches: isLicenseMode && batchesToSend.length ? batchesToSend : undefined,
        send_email: sendEmail,
      };
      onSubmitUpdate(accountId, base);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-wide">
        <h2>{title}</h2>
        <form onSubmit={handleSubmit} className="license-form">
          <input type="text" placeholder="Customer Name" value={customerName}
            onChange={(e) => setCustomerName(e.target.value)} />
          <input type="text" placeholder="Account ID" value={accountId}
            onChange={(e) => setAccountId(e.target.value)} required disabled={mode === 'update'} />
          <input type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))} required />
          <RegionCombobox value={region} onChange={setRegion} required />
          <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            <option value="LICENSE">LICENSE</option>
            <option value="SUBSCRIPTION">SUBSCRIPTION</option>
          </select>
          {!isLicenseMode && (
            <input type="text" placeholder="Registered Nodes Limit" value={registeredNodesLimit}
              onChange={(e) => setRegisteredNodesLimit(e.target.value)} />
          )}

          <SecondaryEmailsEditor
            emails={secondaryEmails}
            onChange={setSecondaryEmails}
            onDraftChange={(d) => { setPendingEmail(d); if (d.trim()) setFormError(''); }}
            onCommit={
              isUpdate && onCommitEmail
                ? (email, operation) => onCommitEmail(accountId, email, operation)
                : undefined
            }
          />

          {isLicenseMode && (
            <>
              {isUpdate && (
                <ExistingBatchEditor batches={existingBatches} onChange={setExistingBatches} />
              )}
              <NewBatchEditor
                batches={newBatches}
                onChange={setNewBatches}
                heading={isUpdate ? 'Add New Batches' : 'Batches'}
              />
            </>
          )}

          <label className="feature-checkbox">
            <input type="checkbox" checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)} />
            Send license email to customer
          </label>
          {isUpdate && (
            <p className="batch-hint">
              Note: <code>send_email</code> is not in the documented v2 update API — it's sent
              best-effort and the backend may ignore it (updates may email regardless).
            </p>
          )}

          {formError && <div className="error-message">{formError}</div>}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
            <button type="submit" className="submit-button">{title}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------- Migration compare ----------

const MigrationCompare: React.FC<{
  v1: AnyLicense;
  proposed: NewV2LicenseRequest;
  formatTimestamp: (t?: number) => string;
  onSubmit: (data: NewV2LicenseRequest) => void;
  onCancel: () => void;
}> = ({ v1, proposed, formatTimestamp, onSubmit, onCancel }) => {
  const [showForm, setShowForm] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  if (showForm) {
    return (
      <V2LicenseForm
        title="Confirm v2 License (from migration)"
        mode="create"
        prefill={{ ...proposed, version: 'v2' }}
        onSubmitCreate={onSubmit}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-wide">
        <h2>Migrate v1 → v2 — Review</h2>
        <div className="migrate-note-row">
          <p className="migrate-note">
            Read-only preview. The v2 payload below is derived from the latest v1 license; the
            <strong> region</strong> is computed server-side from the account's latest deployment
            history. Review and edit it, then confirm to create the v2 license (single POST).
          </p>
          <button type="button" className="raw-toggle" onClick={() => setShowRaw(!showRaw)}>
            {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
          </button>
        </div>
        <div className="compare-grid">
          <div className="compare-col">
            <h3>Existing v1</h3>
            <p><strong>Account:</strong> {v1.account_id}</p>
            <p><strong>Email:</strong> {v1.email}</p>
            <p><strong>Registered Nodes:</strong> {v1.registered_nodes_limit}</p>
            <p><strong>Payment Mode:</strong> {v1.payment_mode || 'N/A'}</p>
            <p><strong>Expires:</strong> {formatTimestamp(v1.expiration_timestamp)}</p>
            {v1.features && v1.features.length > 0 && (
              <ul>{v1.features.map((f, i) => (
                <li key={i}>{f.name}{f.enabled ? ' (on)' : ' (off)'}{f.quota ? `: ${f.quota}` : ''}</li>
              ))}</ul>
            )}
            {showRaw && <pre className="raw-json">{JSON.stringify(v1, null, 2)}</pre>}
          </div>
          <div className="compare-col">
            <h3>Proposed v2</h3>
            {proposed.customer_name && <p><strong>Customer:</strong> {proposed.customer_name}</p>}
            <p><strong>Account:</strong> {proposed.account_id}</p>
            <p><strong>Email:</strong> {proposed.email}</p>
            {proposed.secondary_emails && proposed.secondary_emails.length > 0 && (
              <p><strong>Cc:</strong> {proposed.secondary_emails.join(', ')}</p>
            )}
            <p><strong>Region:</strong> {proposed.region}</p>
            <p><strong>Payment Mode:</strong> {proposed.payment_mode}</p>
            <p><strong>Batches:</strong></p>
            <table className="batch-table">
              <thead><tr><th>Nodes</th><th>Exp(mo)</th><th>Matter</th><th>Camera</th></tr></thead>
              <tbody>
                {(proposed.batches || []).map((b, i) => (
                  <tr key={i}>
                    <td>{b.nodes_limit}</td>
                    <td>{b.expiration_months ?? '-'}</td>
                    <td>{b.matter_fabric_enabled ? '∞' : b.matter_trial_quota ?? '-'}</td>
                    <td>{b.camera_quota ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {showRaw && <pre className="raw-json">{JSON.stringify(proposed, null, 2)}</pre>}
          </div>
        </div>
        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
          <button type="button" onClick={() => setShowForm(true)} className="submit-button">
            Edit &amp; Create v2
          </button>
        </div>
      </div>
    </div>
  );
};

export default LicenseScreen;
